import { useEffect, useRef, useState } from 'react';

export type LeadOpsAction = 'sw' | 'monday' | 'both' | null;

const WORKBENCH_STORAGE_KEY = 'leadops_workbench';

export const useLeadWorkbench = () => {
  const [domainInput, setDomainInput] = useState('');
  const [lastReadDomain, setLastReadDomain] = useState('');
  const [hasFullData, setHasFullData] = useState(false);
  const [hasCrmData, setHasCrmData] = useState(false);
  const [crmSnapshot, setCrmSnapshot] = useState<any>(null);
  const [swSnapshot, setSwSnapshot] = useState<any>(null);
  const [suggestedActions, setSuggestedActions] = useState<any>(null);
  const [lastDomainCanonical, setLastDomainCanonical] = useState('');
  const [lastAction, setLastAction] = useState<LeadOpsAction>(null);

  const lastReadDomainRef = useRef('');
  const crmSnapshotRef = useRef<any>(null);
  const swSnapshotRef = useRef<any>(null);
  const suggestedActionsRef = useRef<any>(null);

  const setLastReadDomainTracked = (domain: string) => {
    lastReadDomainRef.current = domain;
    setLastReadDomain(domain);
  };
  const setCrmSnapshotTracked = (value: any) => {
    crmSnapshotRef.current = value;
    setCrmSnapshot(value);
  };
  const setSwSnapshotTracked = (value: any) => {
    swSnapshotRef.current = value;
    setSwSnapshot(value);
  };
  const setSuggestedActionsTracked = (value: any) => {
    suggestedActionsRef.current = value;
    setSuggestedActions(value);
  };

  const saveWorkbenchState = (domain: string, data: any) => {
    try {
      sessionStorage.setItem(WORKBENCH_STORAGE_KEY, JSON.stringify({
        domain,
        domain_canonical: data?.domain_canonical || domain,
        crm: data?.crm ?? null,
        similarweb: data?.similarweb ?? null,
        suggested_actions: data?.suggested_actions ?? null,
      }));
    } catch {
      // sessionStorage can be unavailable in private browsing contexts.
    }
  };

  const clearWorkbenchState = () => {
    sessionStorage.removeItem(WORKBENCH_STORAGE_KEY);
  };

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(WORKBENCH_STORAGE_KEY);
      if (!raw) return;
      const saved = JSON.parse(raw);
      if (!saved?.domain) return;
      setDomainInput(saved.domain);
      setLastReadDomainTracked(saved.domain);
      setLastDomainCanonical(saved.domain_canonical || saved.domain);
      setCrmSnapshotTracked(saved.crm ?? null);
      setSwSnapshotTracked(saved.similarweb ?? null);
      setSuggestedActionsTracked(saved.suggested_actions ?? null);
      setHasCrmData(Boolean(saved.crm));
      setHasFullData(Boolean(saved.similarweb));
      setLastAction(saved.similarweb && saved.crm ? 'both' : saved.similarweb ? 'sw' : saved.crm ? 'monday' : null);
    } catch {
      clearWorkbenchState();
    }
  }, []);

  return {
    domainInput,
    setDomainInput,
    lastReadDomain,
    lastReadDomainRef,
    setLastReadDomainTracked,
    hasFullData,
    setHasFullData,
    hasCrmData,
    setHasCrmData,
    crmSnapshot,
    crmSnapshotRef,
    setCrmSnapshotTracked,
    swSnapshot,
    swSnapshotRef,
    setSwSnapshotTracked,
    suggestedActions,
    suggestedActionsRef,
    setSuggestedActionsTracked,
    lastDomainCanonical,
    setLastDomainCanonical,
    lastAction,
    setLastAction,
    saveWorkbenchState,
    clearWorkbenchState,
  };
};
